import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nMessage: ${form.message}`;
    window.open(`https://wa.me/917418749974?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Layout>
      <img
        src="/trichy_rockfort_taxi_hero_1776155164304.png"
        alt="contact trichy to velankanni taxi taxi service trichy cab booking trichy to velankanni taxi trichy whatsapp taxi trichy"
        className="hidden"
      />
      <SEOHead
        title="Contact Trichy to Velankanni Taxi - Call, WhatsApp or Book Online"
        description="Contact Trichy to Velankanni Taxi for cab booking. Call +91 74187 49974, WhatsApp us, or fill the contact form. 24/7 taxi service in Trichy."
        keywords="contact trichy to velankanni taxi, taxi phone number trichy, cab booking trichy, to velankanni taxi trichy, outstation taxi trichy, taxi near me trichy"
      />

      <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom text-center">
          <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
            Contact <span className="text-gradient">Us</span>
          </motion.h1>
          <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Ready to book a taxi? Get in touch with us — we're available 24/7 for all your travel needs in Trichy.
          </motion.p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div className="space-y-6">
              <motion.div {...fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Phone, label: "Call Us", value: "+91 74187 49974", href: "tel:+917418749974" },
                  { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: "https://wa.me/917418749974" },
                  { icon: Mail, label: "Email", value: "info@trichytovelankanni.taxi", href: "mailto:info@trichytovelankanni.taxi" },
                  { icon: Clock, label: "Hours", value: "24/7 Available", href: undefined },
                ].map((c) => (
                  <div key={c.label} className="glass-card-hover rounded-2xl p-5">
                    <c.icon className="w-6 h-6 text-primary mb-3" />
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                    {c.label === "Call Us" ? (
                      <div className="flex flex-col gap-1">
                        <a href="tel:+917418749974" className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                          +91 74187 49974
                        </a>
                      </div>
                    ) : c.href ? (
                      <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-semibold text-foreground hover:text-primary transition-colors">
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-foreground">{c.value}</p>
                    )}
                  </div>
                ))}
              </motion.div>

              <motion.div {...fadeUp} className="glass-card rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Our Office</p>
                    <p className="text-sm text-muted-foreground">No.9 kpn complex, melachintamani, Trichy</p>
                  </div>
                </div>
              </motion.div>

              {/* Map */}
              <motion.div {...fadeUp} className="glass-card rounded-2xl overflow-hidden h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15676.657512803853!2d78.6811!3d10.8227!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3baaf50ff2dba5d7%3A0x10267761321000!2sTiruchirappalli%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Trichy to Velankanni Taxi Location"
                />
              </motion.div>
            </div>

            {/* Form */}
            <motion.form
              {...fadeUp}
              onSubmit={handleSubmit}
              className="glass-card rounded-2xl p-6 sm:p-8 h-fit"
            >
              <h2 className="text-xl font-bold text-foreground mb-6">Send Us a Message</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Your Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="+91 74187 49974"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Email (Optional)</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1.5">Message</label>
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-secondary/50 border border-border rounded-lg py-3 px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                    placeholder="Tell us about your travel plans..."
                  />
                </div>
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> Send Message
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
